import Vue from 'vue';
import { getSwiper, getSwiperModules } from '../utils';
import {
    configuratorSlideStyle,
    CONFIGURATOR_IMAGE_MAX_WIDTH,
} from '../utils/configurator-image-url';

// Width used before the slide is measured on mount (desktop viewport).
const DEFAULT_IMAGE_WIDTH = 1280;

Vue.component('configurator-photo-slider-desktop', {
    props: {
        model: {
            value: String,
        },
        siteHandle: {
            value: String,
        },
        imgixUrl: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            swiper: null,
            edition: '',
            color: '',
            exteriorOptions: '',
            interiorOptions: '',
            noLuxuryCountries: [
                'mgMotorsSweden',
                'mgMotorsIceland',
                'mgMotorsNorway',
            ],
            hasInteriorImage: false,
            imageWidth: DEFAULT_IMAGE_WIDTH,
        };
    },

    mounted() {
        this.initSwiper();
        this.setImageWidth();

        EventBus.$on('configurator-set-color', (color) => {
            this.color = color.colorSlug;
            if (!this.swiper) return;
            if (this.$parent.hasInteriorImage) {
                this.swiper.slideToLoop(0, 0, false);
            }
        });

        // Update edition when it changes.
        EventBus.$on('configurator-set-edition', (edition) => {
            this.edition = edition.field_commerceHandle;
            if (!this.swiper) return;
            if (this.$parent.hasInteriorImage !== this.hasInteriorImage) {
                this.initSwiper();
                this.swiper.slideTo(0, 0, false);

                if (!this.$parent.hasInteriorImage) {
                    this.swiper.keyboard.disable();
                }

                return;
            }

            if (this.$parent.hasInteriorImage) {
                this.swiper.slideToLoop(0, 0, false);
                this.swiper.keyboard.enable();
            }
        });

        // Set proper classes when the edition options have changed.
        EventBus.$on('configurator-set-edition-options', (options) => {
            if (!this.swiper) return;
            this.onEditionOptionsChange(options);
        });

        // Slide to proper slide when an edition option has been selected.
        EventBus.$on('configurator-set-edition-option', (option) => {
            if (!this.swiper) return;
            this.onEditionOptionChange(option);
        });

        EventBus.$on('configurator-back-to-the-studio', () => {
            if (!this.swiper) return;
            if (this.$parent.hasInteriorImage !== this.hasInteriorImage) {
                this.initSwiper();
                this.swiper.slideTo(0, 0, false);

                if (!this.$parent.hasInteriorImage) {
                    this.swiper.keyboard.disable();
                }

                return;
            }

            if (this.$parent.hasInteriorImage) {
                this.swiper.slideToLoop(0, 0, false);
            }
        });
    },

    watch: {
        edition(editionValue) {
            const isNoLuxuryCountries = this.noLuxuryCountries.includes(
                this.siteHandle,
            );
            const ehsSpainEditions = {
                luxury: 'luxury-spain',
                comfort: 'comfort-spain',
            };

            if (
                this.siteHandle === 'mgMotorsSpain' &&
                this.model === 'mg-ehs-2023' &&
                ehsSpainEditions[editionValue]
            ) {
                this.edition = ehsSpainEditions[editionValue];
                return;
            }

            if (!isNoLuxuryCountries || this.model !== 'mg4') return;
            if (editionValue === 'luxury') this.edition = 'comfort';
        },
    },

    methods: {
        async initSwiper() {
            const slider = this.$el.querySelector(
                '.configurator-slider__slider',
            );

            const { Swiper } = await getSwiper();
            const { Pagination, Navigation, A11y, Keyboard } =
                await getSwiperModules();

            this.swiper = new Swiper(slider, {
                slidesPerView: 1,
                spaceBetween: 0,
                loop: true,
                centeredSlides: true,
                allowTouchMove: false,
                modules: [Pagination, Navigation, A11y, Keyboard],
                keyboard: {
                    enabled: true,
                },
                uniqueNavElements: true,
                navigation: {
                    nextEl: '.slide-next',
                    prevEl: '.slide-prev',
                },
            });
            this.swiper.init();

            this.hasInteriorImage = this.$parent.hasInteriorImage;
        },

        /**
         * Set proper classes when the edition options have changed.
         *
         * @param {Array} options Array holding all the available options with this edition.
         * @returns {Void}
         */
        onEditionOptionsChange(options) {
            // Collect classes in arrays.
            let exteriorOptions = [];
            let interiorOptions = [];

            // Loop options to update arrays.
            for (let option in options) {
                // Add to the proper class array if this option is selected.
                if (options[option].selected) {
                    if (options[option].field_moveToSliderImage == 'interior') {
                        interiorOptions.push(
                            options[option].field_optionHandle,
                        );
                    }
                    if (options[option].field_moveToSliderImage == 'exterior') {
                        exteriorOptions.push(
                            options[option].field_optionHandle,
                        );
                    }
                }
            }

            // Update class properties.
            this.exteriorOptions = exteriorOptions.join(' ');
            this.interiorOptions = interiorOptions.join(' ');
        },

        /**
         * Slide to proper slide when an edition option has been selected.
         *
         * @param {Object} option The object of the option that has changed.
         * @returns {Void}
         */
        onEditionOptionChange(option) {
            if (!this.$parent.hasInteriorImage) {
                return;
            }

            // Slide to exterior slide if required by this option.
            if (option.field_moveToSliderImage == 'exterior') {
                this.swiper.slideToLoop(0, 0, false);
            }

            // Slide to interior slide if required by this option.
            if (option.field_moveToSliderImage == 'interior') {
                this.swiper.slideToLoop(1, 0, false);
            }
        },

        handleSantanderModalVisibility() {
            EventBus.$emit('handle-santander-modal-visibility');
        },

        handleSCFModalVisibility(scfHandle = null) {
            EventBus.$emit('handle-scf-modal-visibility', scfHandle);
        },

        getSliderAssetClasses(assetType) {
            const modelsWithNumericEditionNames = [
                'mg4-mce-2026',
                'mg4-urban-ev-2026',
            ];
            const isModelWithNumericEditionNames =
                modelsWithNumericEditionNames.includes(this.model);

            // Some model edition names start with numbers, so CSS class names have to be prefixed.
            const editionClass = isModelWithNumericEditionNames
                ? `_${this.edition}`
                : this.edition;

            const classes =
                assetType === 'exterior'
                    ? [
                          this.model,
                          editionClass,
                          this.color,
                          this.exteriorOptions,
                      ]
                    : [this.model, editionClass, this.interiorOptions];

            return classes;
        },

        /**
         * Background-image style for a slide, served via Imgix from the CDN.
         *
         * @param {String} assetType 'exterior' or 'interior'.
         * @returns {Object} A `{ backgroundImage }` style object, or `{}`.
         */
        getSliderImageStyle(assetType) {
            return configuratorSlideStyle({
                imgixBase: this.imgixUrl,
                assetType,
                classes: this.getSliderAssetClasses(assetType),
                width: this.imageWidth,
            });
        },

        setImageWidth() {
            const slide = this.$el.querySelector('.slide-image');
            const width = slide ? slide.clientWidth : 0;
            if (!width) return;
            const dpr = window.devicePixelRatio || 1;
            this.imageWidth = Math.min(
                CONFIGURATOR_IMAGE_MAX_WIDTH,
                Math.round(width * dpr),
            );
        },
    },
});
